import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  Modal,
  Descriptions,
  Typography,
  Table,
  Tag,
  Space,
  Badge,
} from 'antd';

const { Title, Text } = Typography;

const SubjectPreviewModal = ({ open, onClose, subject }) => {
  const { t } = useTranslation();
  const localSubjects = Array.isArray(subject?.local_subjects)
    ? subject.local_subjects
    : [];
  const hasFlatMapping =
    Boolean(subject?.local_subject_code) ||
    Boolean(subject?.local_subject_name);
  const facultyTag =
    localSubjects.length > 0 || hasFlatMapping ? 'FRI' : 'UNIZA';

  const mappings = useMemo(() => {
    const items = [
      ...localSubjects.map((s) => ({
        id: s?.id ?? `${s?.code}-${s?.name}`,
        code: s?.code ?? '',
        name: s?.name ?? '',
      })),
    ];

    if (hasFlatMapping) {
      items.push({
        id: subject?.local_subject_code || `flat-${subject?.id || 'x'}`,
        code: subject?.local_subject_code || '',
        name: subject?.local_subject_name || '',
      });
    }

    const unique = new Map();

    items.forEach((item) => {
      if (!item.code && !item.name) return;
      const key = `${item.code.toLowerCase()}|${item.name.toLowerCase()}`;
      if (!unique.has(key)) {
        unique.set(key, item);
      }
    });

    return Array.from(unique.values());
  }, [
    hasFlatMapping,
    localSubjects,
    subject?.id,
    subject?.local_subject_code,
    subject?.local_subject_name,
  ]);

  const mappingColumns = [
    {
      title: t('Kód predmetu FRI'),
      dataIndex: 'code',
      key: 'code',
      ellipsis: true,
    },
    {
      title: t('Názov predmetu FRI'),
      dataIndex: 'name',
      key: 'name',
      ellipsis: true,
    },
  ];

  const titleExtras = (
    <Space size="small">
      {facultyTag === 'FRI' ? <Tag color="gold">FRI</Tag> : <Tag>UNIZA</Tag>}
    </Space>
  );

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      width={720}
      title={
        <Space size="middle">
          <span>{subject?.name || t('Detail predmetu')}</span>
          {titleExtras}
        </Space>
      }
      destroyOnHidden
    >
      {!subject ? null : (
        <Space orientation="vertical" size={24} style={{ width: '100%' }}>
          <Descriptions
            bordered
            column={1}
            size="middle"
            styles={{ label: { width: 220 } }}
          >
            <Descriptions.Item label={t('Akademický rok')}>
              {subject.academic_year || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Kredity')}>
              {subject.credits ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Uznané kredity')}>
              {subject.recognized_credits ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Priemer známok')}>
              {subject.marks_average ?? '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Rozsah')}>
              {subject.range || '-'}
            </Descriptions.Item>
            <Descriptions.Item label={t('Univerzita')}>
              {subject.university?.name || subject.university_name || '-'}
            </Descriptions.Item>
          </Descriptions>

          <div>
            <Title level={5} style={{ marginBottom: 12 }}>
              {t('Mapovanie na FRI')}
            </Title>
            {mappings.length ? (
              <Table
                size="small"
                rowKey={(r) => r.id}
                columns={mappingColumns}
                dataSource={mappings}
                pagination={false}
                bordered
              />
            ) : (
              <Text type="secondary">
                {t('Žiadne mapovanie na FRI zatiaľ neexistuje.')}
              </Text>
            )}
          </div>
        </Space>
      )}
    </Modal>
  );
};

SubjectPreviewModal.propTypes = {
  open: PropTypes.bool,
  onClose: PropTypes.func,
  subject: PropTypes.object,
};

export default SubjectPreviewModal;
